import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton,
} from '@mui/material';
import { Plus, Trash2, GripVertical } from 'lucide-react';

const navy = '#1e3a5f';

const HEBREW_MONTHS = [
  { value: 7,  label: 'תשרי' },
  { value: 8,  label: 'חשון' },
  { value: 9,  label: 'כסלו' },
  { value: 10, label: 'טבת' },
  { value: 11, label: 'שבט' },
  { value: 12, label: 'אדר א׳' },
  { value: 13, label: 'אדר ב׳' },
  { value: 1,  label: 'ניסן' },
  { value: 2,  label: 'אייר' },
  { value: 3,  label: 'סיון' },
  { value: 4,  label: 'תמוז' },
  { value: 5,  label: 'אב' },
  { value: 6,  label: 'אלול' },
];

const HEBREW_DAYS = [
  { value: 1,  label: "א׳"  }, { value: 2,  label: "ב׳"  }, { value: 3,  label: "ג׳"  },
  { value: 4,  label: "ד׳"  }, { value: 5,  label: "ה׳"  }, { value: 6,  label: "ו׳"  },
  { value: 7,  label: "ז׳"  }, { value: 8,  label: "ח׳"  }, { value: 9,  label: "ט׳"  },
  { value: 10, label: "י׳"  }, { value: 11, label: 'י"א' }, { value: 12, label: 'י"ב' },
  { value: 13, label: 'י"ג' }, { value: 14, label: 'י"ד' }, { value: 15, label: 'ט"ו' },
  { value: 16, label: 'ט"ז' }, { value: 17, label: 'י"ז' }, { value: 18, label: 'י"ח' },
  { value: 19, label: 'י"ט' }, { value: 20, label: "כ׳"  }, { value: 21, label: 'כ"א' },
  { value: 22, label: 'כ"ב' }, { value: 23, label: 'כ"ג' }, { value: 24, label: 'כ"ד' },
  { value: 25, label: 'כ"ה' }, { value: 26, label: 'כ"ו' }, { value: 27, label: 'כ"ז' },
  { value: 28, label: 'כ"ח' }, { value: 29, label: 'כ"ט' }, { value: 30, label: "ל׳"  },
];

const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const PRESET_COLORS = ['#1e3a5f', '#2d5a8f', '#e6c45e', '#d4af37', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#f97316'];

const EMPTY_DAY = { name: '', hebrewDay: 1, hebrewMonth: 7, color: '#1e3a5f' };

/**
 * MoadDaysStep
 * הגדרת ימי המועד — שם, תאריך עברי, צבע badge
 *
 * Props:
 *   formData  { days: [] }
 *   onChange  (partial) => void
 */
const DEFAULT_ENTRY_ZMAN = { key: 'dayEntryTime', icon: 'ShabbatCandles' };
const DEFAULT_EXIT_ZMAN  = { key: 'dayExitTime',  icon: 'MoonStar'       };

const MoadDaysStep = ({ formData, onChange }) => {
  const days = formData?.days || [];
  const [draft, setDraft] = useState(null);

  const updateDays = (updated) => onChange({ days: updated });
  const removeDay = (index) => updateDays(days.filter((_, i) => i !== index));

  const saveDay = () => {
    if (!draft.name.trim()) return;
    // כל יום חדש מקבל מפתחות סנטינל — MoadZmanStep יאפשר עקיפה פר-יום
    updateDays([...days, {
      ...draft,
      entryZman: DEFAULT_ENTRY_ZMAN,
      exitZman:  DEFAULT_EXIT_ZMAN,
    }]);
    setDraft(null);
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        ימי המועד
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 1.5, lineHeight: 1.65 }}>
        כל יום שתוסיף כאן מייצג יום אחד במועד — למשל "א׳ דראש השנה", "ליל הסדר", "א׳ דסוכות".
        לכל יום ייחושב זמן כניסה ויציאה בנפרד על פי התאריך העברי שתגדיר.
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.65 }}>
        בשלבים הבאים תוכל לקבוע לאיזה זמן הלכתי מתייחס כל יום — שקיעה, צאת כוכבים וכד׳ —
        ואותו הגדרה תחול על כל הימים ברשימה. לכן חשוב להכניס כאן את כל ימי המועד הרצויים לפני שממשיכים.
      </Typography>

      {/* Existing days */}
      {days.length > 0 && (
        <Box sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
          {days.map((day, i) => (
            <Box key={i} sx={{
              px: 2, py: 1.25,
              display: 'flex', alignItems: 'center', gap: 1.5,
              bgcolor: i % 2 === 0 ? '#fafafa' : 'white',
              borderBottom: i < days.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}>
              <GripVertical size={14} color="#d1d5db" style={{ flexShrink: 0 }} />
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: day.color || navy, flexShrink: 0 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827', flex: 1 }}>
                {day.name}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0 }}>
                {HEBREW_DAYS.find(d => d.value === day.hebrewDay)?.label || day.hebrewDay} {HEBREW_MONTHS.find(m => m.value === day.hebrewMonth)?.label || ''}
              </Typography>
              <IconButton size="small" onClick={() => removeDay(i)} sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' } }}>
                <Trash2 size={14} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Add form */}
      {draft ? (
        <Box sx={{ border: '1px solid #bae6fd', borderRadius: 2, bgcolor: '#f0f9ff', p: 2, mb: 1.5 }}>
          <Stack spacing={1.5}>
            {/* Name */}
            <TextField
              size="small"
              label="שם היום"
              placeholder="א׳ דראש השנה, א׳ דפסח..."
              value={draft.name}
              onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
              fullWidth
              sx={inputSx}
            />

            {/* Hebrew date */}
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" sx={{ width: 90, flexShrink: 0 }}>
                <InputLabel sx={{ fontSize: '0.82rem' }}>יום</InputLabel>
                <Select
                  label="יום"
                  value={draft.hebrewDay}
                  onChange={(e) => setDraft(d => ({ ...d, hebrewDay: e.target.value }))}
                  sx={{ ...inputSx, fontSize: '0.82rem' }}
                >
                  {HEBREW_DAYS.map(d => (
                    <MenuItem key={d.value} value={d.value} sx={{ fontSize: '0.85rem', fontFamily: 'inherit' }}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ fontSize: '0.82rem' }}>חודש עברי</InputLabel>
                <Select
                  label="חודש עברי"
                  value={draft.hebrewMonth}
                  onChange={(e) => setDraft(d => ({ ...d, hebrewMonth: e.target.value }))}
                  sx={{ ...inputSx, fontSize: '0.82rem' }}
                >
                  {HEBREW_MONTHS.map(m => (
                    <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.82rem' }}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Color */}
            <Box>
              <Typography sx={{ fontSize: '0.8rem', color: '#374151', mb: 0.75, fontWeight: 500 }}>
                צבע badge
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                {PRESET_COLORS.map(c => (
                  <Box
                    key={c}
                    onClick={() => setDraft(d => ({ ...d, color: c }))}
                    sx={{
                      width: 24, height: 24, borderRadius: '50%', bgcolor: c,
                      cursor: 'pointer', flexShrink: 0,
                      border: draft.color === c ? '2.5px solid #111827' : '1.5px solid transparent',
                      boxShadow: draft.color === c ? '0 0 0 2px white inset' : 'none',
                      transition: 'all 0.1s',
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setDraft(null)} sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
                ביטול
              </Button>
              <Button
                variant="contained"
                onClick={saveDay}
                disabled={!draft.name.trim()}
                sx={{
                  bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
                  boxShadow: 'none', borderRadius: '8px',
                  '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
                }}
              >
                הוסף יום
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <>
          <Button
            startIcon={<Plus size={15} />}
            onClick={() => setDraft({ ...EMPTY_DAY })}
            sx={{
              border: '1.5px dashed #d1d5db',
              borderRadius: '10px', color: '#6b7280', textTransform: 'none',
              fontSize: '0.83rem', px: 2, py: 1, width: '100%',
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
            }}
          >
            הוסף יום
          </Button>
          {days.length === 0 && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af', mt: 1 }}>
              לא הוגדרו ימים — יש להוסיף לפחות יום אחד
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default MoadDaysStep;
